import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { api, errMsg, PAYMENT_METHODS } from "../lib/api";
import { useStore } from "../context/Store";
import { SimulatedPay } from "../components/SimulatedPay";

export default function Checkout() {
  const { cart, subtotal, fmt, user, authChecked, currency, clearCart } = useStore();
  const nav = useNavigate();
  const [method, setMethod] = useState("mpesa");
  const [ship, setShip] = useState({ name: "", phone: "", address: "", city: "", country: "Kenya" });
  const [busy, setBusy] = useState(false);
  const [sim, setSim] = useState(null);
  const [cfg, setCfg] = useState({});
  useEffect(() => { api.get("/payments/config").then((r) => setCfg(r.data)); }, []);
  useEffect(() => { if (authChecked && !user) nav("/login", { state: { next: "/checkout" } }); }, [authChecked, user, nav]);
  useEffect(() => { if (user) setShip((s) => ({ ...s, name: s.name || user.name })); }, [user]);

  const shipping = subtotal >= 50 || subtotal === 0 ? 0 : 4.99;
  const live = (id) => id === "stripe" || cfg[`${id}_live`];

  const place = async (e) => {
    e.preventDefault();
    if (!cart.length) return toast.error("Your basket is empty");
    setBusy(true);
    try {
      const { data } = await api.post("/orders", { items: cart.map((i) => ({ product_id: i.id, qty: i.qty })), method, shipping: ship, origin_url: window.location.origin, currency });
      if (data.checkout_url) { clearCart(); window.location.href = data.checkout_url; return; }
      if (data.simulated) { setSim({ order_id: data.order_id, method }); return; }
      clearCart(); toast.success(`Check your phone to approve the ${PAYMENT_METHODS.find((m) => m.id === method).name} payment`); nav(`/payment/success?order=${data.order_id}`);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const field = (k, ph, type = "text") => (
    <input data-testid={`checkout-${k}-input`} required type={type} placeholder={ph} value={ship[k]} onChange={(e) => setShip({ ...ship, [k]: e.target.value })} className="h-12 rounded-xl border px-4 bg-white w-full" />
  );

  return (
    <div className="max-w-6xl mx-auto px-6 py-10" data-testid="checkout-page">
      <h1 className="font-display text-4xl sm:text-5xl font-extrabold">Checkout</h1>
      <form onSubmit={place} className="grid lg:grid-cols-[1fr_380px] gap-10 mt-8">
        <div className="space-y-10">
          <section>
            <h2 className="font-display text-lg font-bold mb-4">1. Delivery details</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {field("name", "Full name")}{field("phone", "Phone (e.g. 0712 345 678)", "tel")}
              <div className="sm:col-span-2">{field("address", "Street address / estate")}</div>
              {field("city", "City / town")}
              <select data-testid="checkout-country-select" value={ship.country} onChange={(e) => setShip({ ...ship, country: e.target.value })} className="h-12 rounded-xl border px-4 bg-white">
                {["Kenya", "Nigeria", "South Africa", "Ghana", "Zimbabwe", "Uganda", "Tanzania", "Rwanda", "Zambia", "Malawi", "Ethiopia", "Cameroon", "Other"].map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
          </section>
          <section>
            <h2 className="font-display text-lg font-bold mb-4">2. Payment method</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {PAYMENT_METHODS.map((m) => (
                <button type="button" key={m.id} data-testid={`payment-method-${m.id}`} onClick={() => setMethod(m.id)}
                  className={`relative text-left p-4 rounded-2xl border-2 bg-white transition-[border-color,transform] hover:-translate-y-0.5 ${method === m.id ? "border-[#C85A32]" : "border-transparent"}`}>
                  <div className="flex items-center gap-3">
                    <span className="w-10 h-10 rounded-full grid place-items-center text-white font-bold text-xs" style={{ background: m.color }}>{m.name.slice(0, 2).toUpperCase()}</span>
                    <div><p className="font-semibold text-sm">{m.name}</p><p className="text-xs text-stone-500">{m.desc}</p></div>
                  </div>
                  {!live(m.id) && <span className="absolute top-2 right-2 text-[10px] font-bold uppercase bg-stone-100 text-stone-500 px-2 py-0.5 rounded-full">Demo</span>}
                  {method === m.id && <Check className="absolute bottom-3 right-3 w-4 h-4 text-[#C85A32]" />}
                </button>
              ))}
            </div>
          </section>
        </div>
        <aside className="bg-white rounded-3xl p-6 h-fit border border-[#C85A32]/10 lg:sticky lg:top-36" data-testid="order-summary">
          <h2 className="font-display text-lg font-bold">Order summary</h2>
          <div className="space-y-3 mt-4 max-h-64 overflow-y-auto">
            {cart.map((i) => (
              <div key={i.id} className="flex gap-3 text-sm"><img src={i.image} alt="" className="w-12 h-12 rounded-lg object-cover" /><span className="flex-1 line-clamp-2">{i.qty} × {i.title}</span><span className="font-semibold">{fmt(i.price_usd * i.qty)}</span></div>
            ))}
          </div>
          <div className="border-t mt-4 pt-4 space-y-2 text-sm">
            <div className="flex justify-between"><span>Subtotal</span><span>{fmt(subtotal)}</span></div>
            <div className="flex justify-between"><span>Delivery</span><span>{shipping ? fmt(shipping) : "Free"}</span></div>
            <div className="flex justify-between font-display text-xl font-extrabold pt-2"><span>Total</span><span data-testid="checkout-total">{fmt(subtotal + shipping)}</span></div>
            {currency !== "USD" && <p className="text-xs text-stone-400">Charged in USD equivalent (M-Pesa in KES)</p>}
          </div>
          <button data-testid="place-order-btn" disabled={busy || !cart.length} className="w-full h-12 mt-6 rounded-full bg-[#C85A32] text-white font-semibold hover:bg-[#A9481F] disabled:opacity-50 transition-colors">
            {busy ? "Processing..." : `Pay with ${PAYMENT_METHODS.find((m) => m.id === method).name}`}
          </button>
        </aside>
      </form>
      {sim && <SimulatedPay sim={sim} total={fmt(subtotal + shipping)} phone={ship.phone} onDone={() => { clearCart(); nav(`/orders?highlight=${sim.order_id}`); }} onClose={() => setSim(null)} />}
    </div>
  );
}
